import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { config } from '../config';
import { t } from '../i18n';
import { GlobalSearch } from '../features/reports/GlobalSearch';
import { Breadcrumbs } from './Breadcrumbs';
import { visibleNavItems } from './navigation';

export function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, can, canAny, logout } = useAuth();

  // Close the mobile drawer after navigating.
  useEffect(() => setMenuOpen(false), [pathname]);

  const visibleItems = visibleNavItems(can, canAny);

  return (
    <div className="min-h-screen lg:flex">
      <a
        href="#main"
        onClick={(event) => {
          // Move the focus ourselves: following the hash alone leaves the focus behind in some browsers.
          event.preventDefault();
          document.getElementById('main')?.focus();
        }}
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:px-3 focus:py-2"
      >
        {t('app.skipToContent')}
      </a>

      <aside
        id="sidebar"
        className={`${menuOpen ? 'flex' : 'hidden'} max-h-[75vh] w-full flex-col border-b border-slate-200 bg-brand-900 text-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:max-h-none lg:w-64 lg:shrink-0 lg:border-b-0`}
      >
        <div className="shrink-0 px-5 py-4 text-lg font-bold tracking-wide">
          {config.productName}
        </div>
        {/* The title stays put; the menu scrolls by itself when it is taller than the screen. */}
        <nav
          aria-label={t('nav.primary')}
          className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-4"
        >
          <ul className="space-y-1">
            {visibleItems.map((item) => (
              <li key={`${item.to}:${item.labelKey}`}>
                <NavLink
                  to={item.to}
                  end
                  className={({ isActive }) =>
                    `block rounded px-3 py-2 text-sm font-medium ${isActive ? 'bg-brand-700' : 'hover:bg-brand-700/60'}`
                  }
                >
                  {t(item.labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium lg:hidden"
            aria-expanded={menuOpen}
            aria-controls="sidebar"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? t('nav.close') : t('nav.open')}
          </button>
          <Breadcrumbs />
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-3 text-sm" aria-label={t('auth.userMenu')}>
            <NavLink to="/account" className="hidden underline sm:inline">
              {user?.fullName}
            </NavLink>
            <button
              type="button"
              onClick={() => void logout()}
              className="rounded border border-slate-300 px-3 py-1.5 font-medium hover:bg-slate-50"
            >
              {t('auth.signOut')}
            </button>
          </div>
        </header>

        <main id="main" tabIndex={-1} className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
